// frontend/src/services/hospitalService.js

import apiService from './api';

class EnhancedHospitalService {
  constructor() {
    this.userLocation = null;
    this.locationWatchId = null;
    this.serverStatus = 'unknown'; 
    this.lastServerCheck = null;
  }

  async checkServerStatus() {
    try {
      const response = await apiService.healthCheck();
      this.serverStatus = response.success ? 'awake' : 'sleeping';
      this.lastServerCheck = Date.now();
      return response;
    } catch (error) {
      this.serverStatus = 'sleeping';
      this.lastServerCheck = Date.now();
      return { success: false, error: error.message };
    }
  }

  async getHospitalProfile(showProgress = false) {
    try {
      if (showProgress) {
        console.log('Fetching hospital profile...');
      }

      const response = await apiService.hospitals.getProfile();
      
      if (response.success && response.data) {
        const hospital = this.enhanceHospitalData([response.data])[0];
        
        return {
          success: true,
          data: hospital,
          isApproved: hospital.isApproved || false,
          verificationStatus: hospital.verificationStatus || 'pending'
        };
      }
      
      if (response.needsRegistration || response.success === false) {
        return {
          success: false,
          message: response.message || 'Hospital profile not found',
          needsRegistration: true
        };
      }
      
      return {
        success: false,
        message: 'Unable to load hospital profile'
      };
      
    } catch (error) {
      console.error('Get Hospital Profile Error:', error);
      
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Hospital profile not found. Please complete registration.',
          needsRegistration: true
        };
      }
      
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        return {
          success: false,
          message: 'Unable to connect to server. Please try again.',
          canRetry: true
        };
      }
      
      return this.handleNetworkError(error, 'hospital profile');
    }
  }
 
  async getHospitalSettings(showProgress = false) {
    try {
      if (showProgress) {
        console.log('Fetching hospital settings...');
      }

      const response = await apiService.hospitals.getSettings();
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data
        };
      }
      
      if (response.needsRegistration || response.success === false) {
        return {
          success: false,
          message: response.message || 'Hospital profile not found',
          needsRegistration: true
        };
      }
      
      return {
        success: false,
        message: 'Unable to load hospital settings'
      };
      
    } catch (error) {
      console.error('Get Hospital Settings Error:', error);
      
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Hospital profile not found. Please complete registration.',
          needsRegistration: true
        };
      }
      
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        return {
          success: false,
          message: 'Unable to connect to server. Please try again.',
          canRetry: true,
          networkError: true
        };
      }
      
      return this.handleNetworkError(error, 'hospital settings');
    }
  }

  async updateHospitalSettings(settingsData, showProgress = false) {
    try {
      if (showProgress) {
        console.log('Updating hospital settings...');
      }

      const response = await apiService.hospitals.updateSettings(settingsData);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: 'Settings updated successfully'
        };
      }
      
      return {
        success: false,
        message: response.message || 'Failed to update settings'
      };
      
    } catch (error) {
      console.error('Update Hospital Settings Error:', error);
      
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Hospital profile not found. Please complete registration first.',
          needsRegistration: true
        };
      }
      
      return this.handleNetworkError(error, 'update settings');
    }
  }

  async deleteHospitalImage(imageIndex, showProgress = false) {
    try {
      if (showProgress) {
        console.log(`Deleting image ${imageIndex}...`);
      }

      const response = await apiService.hospitals.deleteImage(imageIndex);
      
      if (response.success) {
        return {
          success: true,
          message: 'Image deleted successfully'
        };
      }
      
      return {
        success: false,
        message: response.message || 'Failed to delete image'
      };
      
    } catch (error) {
      console.error('Delete Hospital Image Error:', error);
      return this.handleNetworkError(error, 'delete image');
    }
  }

  async setPrimaryHospitalImage(imageIndex, showProgress = false) {
    try {
      if (showProgress) {
        console.log(`Setting image ${imageIndex} as primary...`);
      }

      const response = await apiService.hospitals.setPrimaryImage(imageIndex);
      
      if (response.success) {
        return {
          success: true,
          message: 'Primary image updated successfully'
        };
      }
      
      return {
        success: false,
        message: response.message || 'Failed to update primary image'
      };
      
    } catch (error) {
      console.error('Set Primary Image Error:', error);
      return this.handleNetworkError(error, 'set primary image');
    }
  }

  async getHospitals(params = {}, showProgress = false) {
    try {
      if (!this.lastServerCheck || Date.now() - this.lastServerCheck > 300000) {
        if (showProgress) {
          console.log('Checking server status...');
        }
        await this.checkServerStatus();
      }

      // Only use userLocation if it was explicitly set this session
      if (this.userLocation && !params.latitude && !params.longitude) {
        params.latitude = this.userLocation.latitude;
        params.longitude = this.userLocation.longitude;
      }

      if (showProgress) {
        console.log('Fetching hospitals...');
      }

      const data = await apiService.hospitals.getAll(params);
      
      if (data.success && data.data) {
        const hospitals = this.enhanceHospitalData(data.data.hospitals || []);
        
        return {
          success: true,
          data: {
            hospitals,
            pagination: data.data.pagination,
            hasUserLocation: !!this.userLocation
          }
        };
      }
      
      return {
        success: false,
        message: data.message || 'Failed to fetch hospitals',
        data: {
          hospitals: [],
          pagination: null,
          hasUserLocation: !!this.userLocation
        }
      };
    } catch (error) {
      console.error('Hospital Service Error:', error);
      
      let userMessage = 'Failed to fetch hospitals';
      let suggestions = [];

      if (error.code === 'ECONNABORTED') {
        userMessage = 'The request is taking longer than usual. The server might be starting up.';
        suggestions = [
          'Please wait a moment and try again',
          'The server may be in sleep mode and needs time to wake up'
        ];
      } else if (error.code === 'ERR_NETWORK') {
        userMessage = 'Network connection issue detected';
        suggestions = [
          'Check your internet connection',
          'Try refreshing the page',
          'Switch to a different network if possible'
        ];
      } else if (error.response?.status === 404) {
        userMessage = 'Hospital service is temporarily unavailable';
        suggestions = ['Please try again in a few moments'];
      } else if (error.response?.status >= 500) {
        userMessage = 'Server is experiencing issues';
        suggestions = ['Please try again later'];
      }

      return {
        success: false,
        message: userMessage,
        error: error.message,
        suggestions,
        canRetry: ['ECONNABORTED', 'ERR_NETWORK', 'ETIMEDOUT'].includes(error.code)
      };
    }
  }

  async getNearbyHospitals(params = {}, showProgress = false) {
    try {
      // Ensure we have location coordinates
      if (!params.latitude || !params.longitude) {
        if (showProgress) {
          console.log('Getting location...');
        }
        
        if (this.userLocation) {
          params.latitude = this.userLocation.latitude;
          params.longitude = this.userLocation.longitude;
        } else {
          return {
            success: false,
            message: 'Location access is required to find nearby hospitals',
            needsLocation: true
          };
        }
      }

      if (showProgress) {
        console.log('Searching nearby hospitals...');
      }

      const response = await apiService.hospitals.getNearby(params);
      
      if (response.success) {
        const hospitals = this.enhanceHospitalData(response.data.hospitals || []);
        
        return {
          success: true,
          data: {
            hospitals,
            total: response.data.total,
            searchRadius: response.data.searchRadius,
            userLocation: response.data.userLocation
          }
        };
      }
      
      return {
        success: false,
        message: response.data.message || 'Failed to fetch nearby hospitals'
      };
    } catch (error) {
      console.error('Nearby Hospitals Error:', error);
      return this.handleNetworkError(error, 'nearby hospitals');
    }
  }

  async getHospitalById(id, includeDistance = true, showProgress = false) {
    try {
      const params = {};
      
      // Add user location for distance calculation if available
      if (includeDistance && this.userLocation) {
        params.userLat = this.userLocation.latitude;
        params.userLon = this.userLocation.longitude;
      }

      if (showProgress) {
        console.log(`Fetching hospital details for ID: ${id}`);
      }
      
      const response = await apiService.hospitals.getById(id, params);
      
      if (response.success) {
        const hospital = this.enhanceHospitalData([response.data])[0];
        
        return {
          success: true,
          data: hospital
        };
      }
      
      return {
        success: false,
        message: response.message || 'Hospital not found'
      };
    } catch (error) {
      console.error('Get Hospital By ID Error:', error);
      return this.handleNetworkError(error, 'hospital details');
    }
  }

  handleNetworkError(error, context = 'data') {
    let userMessage = `Failed to fetch ${context}`;
    let suggestions = [];
    let canRetry = false;

    if (error.code === 'ECONNABORTED') {
      userMessage = `Loading ${context} is taking longer than usual`;
      suggestions = [
        'The server might be starting up, please wait and try again',
        'This usually takes 30-60 seconds on first request'
      ];
      canRetry = true;
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      userMessage = 'Connection problem detected';
      suggestions = [
        'Check your internet connection',
        'Try refreshing the page',
        'The server might be temporarily unavailable'
      ];
      canRetry = true;
    } else if (error.response?.status === 404) {
      userMessage = `${context.charAt(0).toUpperCase() + context.slice(1)} not found`;
      suggestions = ['Please try a different search or check the URL'];
    } else if (error.response?.status >= 500) {
      userMessage = 'Server error occurred';
      suggestions = ['Please try again in a few moments'];
      canRetry = true;
    }

    return {
      success: false,
      message: userMessage,
      error: error.message,
      suggestions,
      canRetry,
      errorCode: error.code,
      statusCode: error.response?.status
    };
  }

  /**
   * Get current location - ALWAYS requests fresh location permission
   * Does NOT use cached/stored location
   */
  async getCurrentLocation(options = {}) {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({
          success: false,
          message: 'Geolocation is not supported by this browser',
          canRetry: false
        });
        return;
      }

      // Clear any existing location data to force fresh request
      this.userLocation = null;

      const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0, // Always get fresh location
        ...options
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          };
          
          // Store in memory for current session only
          this.userLocation = location;
          
          resolve({
            success: true,
            data: location,
            message: 'Location retrieved successfully'
          });
        },
        (error) => {
          let message = 'Unable to get your location';
          let canRetry = false;
          let suggestions = [];
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location access denied. Please allow location access to find nearby hospitals.';
              suggestions = [
                'Click the location icon (🔒 or ⓘ) in your browser\'s address bar',
                'Select "Allow" for location permissions',
                'Refresh the page and try again',
                'Check your browser\'s privacy settings',
                'Make sure location services are enabled on your device'
              ];
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Location information is unavailable';
              suggestions = [
                'Check that your device\'s location services are enabled',
                'Make sure you have a stable internet connection',
                'Try moving to an area with better GPS signal',
                'Try again in a few moments'
              ];
              canRetry = true;
              break;
            case error.TIMEOUT:
              message = 'Location request timed out';
              suggestions = [
                'Check your internet connection',
                'Make sure GPS is enabled on your device',
                'Try again',
                'Move to an area with better signal'
              ];
              canRetry = true;
              break;
            default:
              message = 'An unknown error occurred while retrieving location';
              suggestions = ['Please try again', 'Check your device settings'];
              canRetry = true;
              break;
          }
          
          console.error('Geolocation error:', error);
          resolve({
            success: false,
            message,
            suggestions,
            canRetry,
            error: error.code
          });
        },
        defaultOptions
      );
    });
  }

  /**
   * Start watching location changes
   */
  startLocationWatch(callback, options = {}) {
    if (!navigator.geolocation) {
      callback({
        success: false,
        message: 'Geolocation is not supported',
        canRetry: false
      });
      return null;
    }

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 60000,
      maximumAge: 0,
      ...options
    };

    this.locationWatchId = navigator.geolocation.watchPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
        
        // Store in memory only
        this.userLocation = location;
        
        callback({
          success: true,
          data: location
        });
      },
      (error) => {
        callback({
          success: false,
          message: 'Location watch failed',
          error: error.code,
          canRetry: error.code !== error.PERMISSION_DENIED
        });
      },
      defaultOptions
    );

    return this.locationWatchId;
  }

  /**
   * Stop watching location
   */
  stopLocationWatch() {
    if (this.locationWatchId !== null) {
      navigator.geolocation.clearWatch(this.locationWatchId);
      this.locationWatchId = null;
    }
  }

  /**
   * Clear current location data
   */
  clearLocation() {
    this.userLocation = null;
  }

  /**
   * Get current stored location (session only, no persistence)
   */
  getStoredLocation() {
    return this.userLocation || null;
  }

  enhanceHospitalData(hospitals) {
    if (!Array.isArray(hospitals)) {
      console.warn('Expected array of hospitals, received:', typeof hospitals);
      return [];
    }

    return hospitals.map(hospital => {
      try {
        const enhanced = { ...hospital };
        
        enhanced.primaryPhone = this.extractPrimaryPhone(hospital.contact);
        
        if (hospital.distance !== undefined && hospital.distance !== null) {
          enhanced.distanceText = this.formatDistance(hospital.distance);
        }
        
        enhanced.hasPhone = !!enhanced.primaryPhone;
        enhanced.hasEmergency = hospital.operatingHours?.is24Hours || 
                               this.hasEmergencyService(hospital.services);
        enhanced.isVerified = hospital.user?.isVerified || hospital.isVerified || false;
        
        if (hospital.operatingHours || hospital.currentStatus) {
          enhanced.operatingStatus = hospital.currentStatus || 
                                   this.getCurrentOperatingStatus(hospital.operatingHours);
        }
        
        enhanced.keyServices = this.extractKeyServices(hospital.services);
        
        enhanced.name = hospital.name || 'Unknown Hospital';
        enhanced.location = hospital.location || {};
        enhanced.rating = hospital.rating || { average: 0, totalReviews: 0 };
        
        return enhanced;
      } catch (error) {
        console.error('Error enhancing hospital data:', error, hospital);
        return hospital;
      }
    });
  }

  extractPrimaryPhone(contact) {
    if (!contact || typeof contact !== 'object') return null;
    
    return contact.phone || 
           contact.emergency || 
           contact.appointment ||
           contact.reception ||
           contact.main ||
           null;
  }

  formatDistance(distance) {
    if (distance === null || distance === undefined || isNaN(distance)) return null;
    
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m away`;
    } else if (distance < 10) {
      return `${distance.toFixed(1)}km away`;
    } else {
      return `${Math.round(distance)}km away`;
    }
  }

  hasEmergencyService(services) {
    if (!services || !Array.isArray(services)) return false;
    
    const emergencyKeywords = ['emergency', 'urgent', 'trauma', '24/7', 'critical', 'er', 'casualty'];
    return services.some(service => {
      if (!service || typeof service.name !== 'string') return false;
      return emergencyKeywords.some(keyword => 
        service.name.toLowerCase().includes(keyword)
      );
    });
  }

  getCurrentOperatingStatus(operatingHours) {
    if (!operatingHours || typeof operatingHours !== 'object') {
      return { status: 'unknown', text: 'Hours not available', color: 'gray' };
    }
    
    if (operatingHours.is24Hours) {
      return { status: 'open', text: '24/7 Open', color: 'green' };
    }
    
    try {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTime = currentHour * 60 + currentMinutes;
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const currentDay = days[now.getDay()];
      
      const todayHours = operatingHours[currentDay];
      if (!todayHours || todayHours.isClosed || !todayHours.open || !todayHours.close) {
        return { status: 'closed', text: 'Closed Today', color: 'red' };
      }
      
      const openParts = todayHours.open.split(':');
      const closeParts = todayHours.close.split(':');
      
      if (openParts.length !== 2 || closeParts.length !== 2) {
        return { status: 'unknown', text: 'Invalid hours format', color: 'gray' };
      }
      
      const openHour = parseInt(openParts[0]);
      const openMin = parseInt(openParts[1]) || 0;
      const closeHour = parseInt(closeParts[0]);
      const closeMin = parseInt(closeParts[1]) || 0;
      
      const openTime = openHour * 60 + openMin;
      const closeTime = closeHour * 60 + closeMin;
      
      if (currentTime >= openTime && currentTime < closeTime) {
        return { 
          status: 'open', 
          text: `Open until ${todayHours.close}`, 
          color: 'green' 
        };
      } else if (currentTime < openTime) {
        return { 
          status: 'closed', 
          text: `Opens at ${todayHours.open}`, 
          color: 'orange' 
        };
      } else {
        return { 
          status: 'closed', 
          text: 'Closed', 
          color: 'red' 
        };
      }
    } catch (error) {
      console.error('Error calculating operating status:', error);
      return { status: 'unknown', text: 'Hours not available', color: 'gray' };
    }
  }

  extractKeyServices(services) {
    if (!services || !Array.isArray(services)) return [];
    
    const priorityServices = [
      'emergency', 'cardiology', 'neurology', 'orthopedic', 
      'pediatric', 'maternity', 'surgery', 'radiology', 'icu'
    ];
    
    try {
      const sorted = services.sort((a, b) => {
        if (!a || !a.name || !b || !b.name) return 0;
        
        const aIndex = priorityServices.findIndex(p => 
          a.name.toLowerCase().includes(p));
        const bIndex = priorityServices.findIndex(p => 
          b.name.toLowerCase().includes(p));
        
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      });
      
      return sorted.slice(0, 3);
    } catch (error) {
      console.error('Error extracting key services:', error);
      return services.slice(0, 3);
    }
  }

  async searchHospitals(query, filters = {}, showProgress = false) {
    try {
      const params = {
        search: query,
        ...filters
      };
      
      if (this.userLocation && !params.latitude && !params.longitude) {
        params.latitude = this.userLocation.latitude;
        params.longitude = this.userLocation.longitude;
        params.sortBy = 'distance';
      }
      
      return await this.getHospitals(params, showProgress);
    } catch (error) {
      console.error('Search Hospitals Error:', error);
      return this.handleNetworkError(error, 'search results');
    }
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    
    const R = 6371;
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    
    const lat1Rad = this.toRad(lat1);
    const lat2Rad = this.toRad(lat2);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.sin(dLon / 2) * Math.sin(dLon / 2) * 
              Math.cos(lat1Rad) * Math.cos(lat2Rad);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    
    return Math.round(d * 10) / 10;
  }

  toRad(value) {
    return value * Math.PI / 180;
  }

  validateHospitalData(data) {
    const errors = [];
    
    if (!data || typeof data !== 'object') {
      errors.push('Invalid hospital data format');
      return { isValid: false, errors };
    }
    
    if (!data.name || typeof data.name !== 'string' || data.name.trim().length < 3) {
      errors.push('Hospital name must be at least 3 characters long');
    }
    
    if (!data.location || !data.location.address) {
      errors.push('Hospital address is required');
    }
    
    if (!data.contact || !this.extractPrimaryPhone(data.contact)) {
      errors.push('At least one phone number is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  async retryRequest(requestFn, maxRetries = 3, baseDelay = 1000) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        if (attempt === maxRetries) {
          throw error;
        }
        
        const delay = baseDelay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
}

// Create singleton instance
const enhancedHospitalService = new EnhancedHospitalService();

export { enhancedHospitalService as hospitalService };